import { flatten } from "lodash";
import { generateUuid } from "lib/utils/socialtravel";
import * as MoneySum from "lib/utils/MoneySum";
import OrderDecorator from "./OrderDecorator";
import ProductScheduleSlotDecorator from "./ProductScheduleSlotDecorator";
import OrderedProductOrderFieldDecorator from "./OrderedProductOrderFieldDecorator";
import OrderedProductPricelistItemDecorator from "./OrderedProductPricelistItemDecorator";
import OrderCustomerParticipantDecorator from "./OrderCustomerParticipantDecorator";
import OrderInstallmentDecorator from "./OrderInstallmentDecorator";
import OrderItemDecorator from "./OrderItemDecorator";
import OrderTransferDecorator from "./OrderTransferDecorator";
import PaymentProfileDecorator from "./PaymentProfileDecorator";

const cleanModelId = (model) => ({ ...model, id: null, uuid: generateUuid() });

export default class OrderFullDecorator extends OrderDecorator {
  static build(data = {}) {
    const order = new OrderFullDecorator({
      type: "Order",
      status: "building",
      booking_type: "static_date_booking",
      manual: true,
      marketplace: false,
      product_name: null,
      product_additional_pickup_instructions: null,
      product_highlights: [],
      product_included_items: [],
      product_excluded_items: [],
      product_customer_requirements: [],
      product_customer_hints: [],

      ordered_product_pricelist_items: [],
      order_customer_participants: [],
      ordered_product_order_fields: [],
      order_items: [],
      installments: [],
      order_transfers: [],
      possible_payment_profiles: [],

      product_schedule_slot: null,
      stock_useby_starts_at: null,
      stock_useby_ends_at: null,

      ...data,
    });
    order.cacheProductAttributes();
    order.recalculate();
    return order;
  }

  static clone(order) {
    return new OrderFullDecorator({
      ...cleanModelId(order),
      ordered_product_pricelist_items: order.ordered_product_pricelist_items.map((model) => ({
        ...cleanModelId(model),
        ordered_product_order_fields: model.ordered_product_order_fields.map(cleanModelId),
      })),
      order_customer_participants: order.order_customer_participants.map((model) => ({
        ...cleanModelId(model),
        ordered_product_order_fields: model.ordered_product_order_fields.map(cleanModelId),
      })),
      ordered_product_order_fields: order.ordered_product_order_fields.map(cleanModelId),
      order_items: order.order_items.map(cleanModelId),
      installments: order.installments.map(cleanModelId),
      order_transfers: order.order_transfers.map(cleanModelId),
    });
  }

  constructor({ customer_display_name, customer_full_name, customer_email, customer_phone_number, ...data }) {
    super({
      ...data,

      product_schedule_slot: data.product_schedule_slot && new ProductScheduleSlotDecorator(data.product_schedule_slot),

      ordered_product_order_fields: data.ordered_product_order_fields
        .map((x) => new OrderedProductOrderFieldDecorator(x))
        .sort((a, b) => a.product_order_field.position - b.product_order_field.position),

      ordered_product_pricelist_items: data.ordered_product_pricelist_items
        .map((x) => new OrderedProductPricelistItemDecorator(x))
        .sort((a, b) => a.product_pricelist_item.position - b.product_pricelist_item.position),

      order_customer_participants: data.order_customer_participants
        .map((x) => new OrderCustomerParticipantDecorator(x))
        .sort((a, b) => a.position - b.position),

      installments: data.installments
        .map((x) => new OrderInstallmentDecorator(x))
        .sort((a, b) => a.position - b.position),

      order_items: data.order_items.map((x) => new OrderItemDecorator(x)).sort((a, b) => a.position - b.position),

      order_transfers: data.order_transfers.map((x) => new OrderTransferDecorator(x)),

      possible_payment_profiles: data.possible_payment_profiles.map((x) => new PaymentProfileDecorator(x)),
    });
  }

  recalculate() {
    this.total_amount_sum = MoneySum.sum(this.order_items.map((oi) => oi.total_price).filter((a) => a && a.value));
    this.customer_paid_amount_sum = MoneySum.sum(
      this.order_transfers.filter((tr) => tr.sender === "customer").map((tr) => tr.amount)
    );
    this.customer_received_amount_sum = [];
  }

  recalculateParticipantsIsExtraAttribute() {
    this.ordered_product_pricelist_items.forEach((orderedItem) => {
      const orderedItemParticipants = this.order_customer_participants.filter(
        (p) => p.ordered_product_pricelist_item && p.ordered_product_pricelist_item.uuid === orderedItem.uuid
      );
      orderedItemParticipants.forEach((p) => {
        p.is_extra = orderedItemParticipants.length > orderedItem.persons_count;
      });
    });
  }

  get stock_start_date() {
    return this.stock_starts_at && moment.parseZone(this.stock_starts_at).format(moment.RUBY_DATE_FORMAT);
  }

  get stock_end_date() {
    return this.stock_ends_at && moment.parseZone(this.stock_ends_at).format(moment.RUBY_DATE_FORMAT);
  }

  get customer_display_name() {
    return this.customer_full_name || this.customer_email || this.customer_phone_number;
  }

  get customer_full_name() {
    return this.getPresetOrderFieldValue("customer_full_name");
  }

  get customer_email() {
    return this.getPresetOrderFieldValue("customer_email");
  }

  get customer_phone_number() {
    return this.getPresetOrderFieldValue("customer_phone_number");
  }

  get customer_note() {
    return this.getPresetOrderFieldValue("customer_note");
  }

  get remaining_installment_amount_sum() {
    const amounts = this.installments
      .filter((inst) => inst.amount_type !== "remaining_amount")
      .map((inst) => {
        if (inst.amount_type === "exact_amount") {
          return inst.amount;
        } else if (inst.amount_type === "percentage_amount") {
          return {
            value: (this.total_base_amount.value * (inst.amount_percentage || 0)) / 100,
            currency: this.total_base_amount.currency,
          };
        } else {
          throw new Error("Unknown amount_type");
        }
      })
      .filter((amount) => !!amount);

    return MoneySum.subtract(this.total_amount_sum, amounts).filter((money) => money.value >= 0);
  }

  getPresetOrderFieldValue(preset_id) {
    const orderedField = (this.ordered_product_order_fields || []).find(
      (orderedField) => orderedField.product_order_field.preset_id === preset_id
    );
    return orderedField ? orderedField.value : null;
  }

  get all_ordered_product_order_fields() {
    return this.ordered_product_order_fields.concat(
      flatten(this.order_customer_participants.map((p) => p.ordered_product_order_fields))
    );
  }

  get priced_ordered_fields() {
    return this.ordered_product_order_fields
      .filter((orderedField) => orderedField.visible)
      .filter((orderedField) => orderedField.product_order_field.has_pricing);
  }

  get customer_ordered_fields() {
    return this.ordered_product_order_fields
      .filter((orderedField) => orderedField.visible)
      .filter((orderedField) => !orderedField.product_order_field.has_pricing);
  }

  get payment_profile() {
    return this.possible_payment_profiles.find((pp) => pp.id === this.payment_profile_id);
  }

  get marketplace_commision_amount() {
    if (this.marketplace) {
      return this.agent_commision_amount;
    }
  }

  get marketplace_service_fee_amount() {
    if (this.marketplace) {
      return this.agent_service_fee_amount;
    }
  }

  cacheProductAttributes() {
    if (this.product) {
      this.booking_type = this.product.booking_type;
      this.product_price_currency = this.product.price_currency;
      this.product_name = this.product.name;
      this.product_highlights = this.product.highlights;
      this.product_customer_hints = this.product.customer_hints;
    }

    if (this.product_variant) {
      this.product_variant_name = this.product_variant.name;
      this.product_variant_short_description = this.product_variant.short_description;
      this.product_included_items = this.product_variant.included_items;
      this.product_excluded_items = this.product_variant.excluded_items;
      this.product_customer_requirements = this.product_variant.customer_requirements;
      this.product_additional_pickup_instructions = this.product_variant.additional_pickup_instructions;
      this.stock_type = this.product_variant.stock_type;
      this.requires_company_approval = this.product_variant.nb_requires_company_approval;
    }

    this.cacheSlotAttributes();
  }

  cacheSlotAttributes() {
    if (this.booking_type == "static_date_booking" && this.product_schedule_slot) {
      this.stock_starts_at = this.product_schedule_slot.starts_at;
      this.stock_ends_at = this.product_schedule_slot.ends_at;
      this.stock_includes_time = this.product_schedule_slot.includes_time;
      this.requires_company_approval =
        this.product_schedule_slot.availability_type === "available_with_company_approval";
      this.stock_type = this.product_schedule_slot.stock_type;
    }
  }
}



// WEBPACK FOOTER //
// ./src/lib/decorators/OrderFullDecorator.js